/**
 * castHeadshotStore — characterId → headshot URL, as Unity's `setCharacterHeadshots` reports it.
 *
 * Headshots are a CACHE artifact, not project data: Unity renders a 256² portrait per cast member
 * into the same hidden per-project cache folder that holds the shot-thumbnail JPGs, hash-skipping
 * anything that has not changed, and hands the files over as `file://…/char_{id}.png?v=<ticks>`
 * URLs (the `PathToFileUrl` convention `bridge/features/fullImage.ts` documents — a file:// URL
 * costs no JS heap, a base64 round trip costs megabytes per portrait). Nothing here is persisted
 * and nothing is written back: the `.previs` carries no headshot column, deliberately, because a
 * render output that can be regenerated from the recipe has no business bloating every project
 * file and forcing a versioned schema migration.
 *
 * REPLACE semantics, which is the whole contract: each message is the FULL set of headshots that
 * exist on disk for the open project, so a character missing from the payload has no headshot and
 * must lose the one it was showing. Merging would strand a portrait belonging to a cast member the
 * pass deleted.
 *
 * Session-scoped and cleared on a FRESH `setSqlitePath` (features/timeline.ts), because character
 * ids are per-.previs: the next project's character 12 must not be served this one's face.
 */
import { create } from 'zustand';

export interface CastHeadshotEntry {
  characterId: number;
  url: string;
}

export interface CastHeadshotState {
  /** characterId → absolute `file://` (embedded) URL. Empty until Unity sends the first set. */
  urls: Record<number, string>;
  /** Replace the whole map — see the REPLACE note above. */
  replaceAll: (entries: CastHeadshotEntry[]) => void;
  clear: () => void;
}

export const useCastHeadshotStore = create<CastHeadshotState>((set) => ({
  urls: {},
  replaceAll: (entries) => {
    const urls: Record<number, string> = {};
    for (const e of entries) urls[e.characterId] = e.url;
    set({ urls });
  },
  clear: () => set({ urls: {} }),
}));

/** The one sanctioned read. A plain function of the state so it serves both call shapes with no
 *  second opinion: `useCastHeadshotStore(s => selectCastHeadshotUrl(s, id))` where a render must
 *  react, `selectCastHeadshotUrl(useCastHeadshotStore.getState(), id)` where a handler reads once.
 *  `id` is nullable because a dialogue-derived cast row carries no characterid at all. */
export const selectCastHeadshotUrl = (
  s: CastHeadshotState,
  id: number | null,
): string | undefined => (id === null ? undefined : s.urls[id]);
